import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../lib/utils.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog.js";
import { Button } from "./button.js";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

// Plain 50% scrim, no blur: backdrop-blur repaints the whole viewport on
// every animation frame (janky on low-end mobile) and reads heavier than the
// quiet chrome we're after. duration-200 keeps the fade in lockstep with
// DialogContent so the scrim never outlives the card.
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/50 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * DialogContent — desktop modal that auto-converts to a bottom sheet on
 * narrow viewports. The same component drives both shapes so callers don't
 * need to pick a layout per device. Below sm: pinned to the bottom edge, full
 * width, rounded top corners, safe-area-aware. At sm+: classic centered card.
 *
 * Internal content should not assume centering; use sticky <DialogFooter> to
 * keep actions visible while the body scrolls (esp. on mobile keyboards).
 *
 * `preventDismiss` blocks the implicit close paths (outside click, Escape)
 * while leaving the explicit ones (X, Cancel, programmatic) working — pass
 * the form's dirty flag so an accidental backdrop click can't discard
 * typed work.
 *
 * `bare` strips the built-in chrome padding/gap AND the X close button so
 * fully self-chromed surfaces (the ⌘K palette) can own their internal
 * layout. This must be a prop, not a `p-0` override: twMerge can only
 * cancel utilities with a matching variant prefix, so `p-0` in className
 * kills `p-5` but leaves `sm:p-6` standing.
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    preventDismiss?: boolean;
    bare?: boolean;
  }
>((
  {
    className,
    children,
    preventDismiss,
    bare,
    onInteractOutside,
    onEscapeKeyDown,
    "aria-describedby": ariaDescribedBy,
    ...props
  },
  ref,
) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onInteractOutside={(event) => {
        if (preventDismiss) event.preventDefault();
        onInteractOutside?.(event);
      }}
      onEscapeKeyDown={(event) => {
        if (preventDismiss) event.preventDefault();
        onEscapeKeyDown?.(event);
      }}
      // Compact action dialogs can be fully described by their title and
      // controls. Passing this explicitly lets callers intentionally omit a
      // redundant description without triggering Radix's missing-copy warning.
      aria-describedby={ariaDescribedBy}
      className={cn(
        // Mobile: bottom sheet that takes full width and up to 92dvh height.
        "fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] w-full flex-col rounded-t-2xl border border-b-0 bg-background shadow-xl duration-200 ease-out motion-reduce:animate-none",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:slide-out-to-bottom-96 data-[state=open]:slide-in-from-bottom-96",
        // Tablet+ (sm:): pop back to centered card. tailwindcss-animate's
        // enter/exit keyframes REPLACE the whole transform, so the slide
        // classes must re-state the -translate-x-1/2/-translate-y-1/2
        // centering (shadcn's "compensators"). left-1/2 + top-[48%] means the
        // card fades/zooms in around its resting spot with a 2%-of-height
        // upward settle — zeroing the slide instead drops the centering from
        // the keyframe and every dialog flies in diagonally from lower-right.
        "sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[85vh] sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:border-b",
        "sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]",
        "sm:data-[state=closed]:zoom-out-95 sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%]",
        // Default chrome padding lives behind the flag (see `bare` above).
        !bare && "gap-4 p-5 pb-safe-or-4 sm:p-6 sm:pb-6",
        className,
      )}
      {...props}
    >
      {children}
      {!bare && (
        <DialogPrimitive.Close
          aria-label="Close"
          className="absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:right-4 sm:top-4 sm:h-9 sm:w-9"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-1.5 pr-10 text-left sm:pr-8", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

/**
 * DialogBody — the scrollable middle region between DialogHeader and
 * DialogFooter. min-h-0 lets it shrink to DialogContent's max-height so
 * long content scrolls here (keeping the sticky footer pinned) instead of
 * spilling past the dialog border. Negative margins bleed through
 * DialogContent's padding so the scrollbar hugs the dialog edge.
 */
const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "-mx-5 min-h-0 flex-1 overflow-y-auto px-5 sm:-mx-6 sm:px-6",
      className,
    )}
    {...props}
  />
);
DialogBody.displayName = "DialogBody";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Mobile: sticky footer pinned above the safe-area inset so primary
      // actions stay reachable while content scrolls and the keyboard is up.
      "sticky bottom-0 -mx-5 -mb-5 mt-2 flex flex-col-reverse gap-2 border-t bg-background px-5 py-3 pb-safe-or-3",
      "sm:static sm:m-0 sm:border-t-0 sm:bg-transparent sm:p-0 sm:pb-0 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export type ConfirmationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  pending?: boolean;
  onConfirm: () => void;
};

export type ConfirmationRequest = Omit<
  ConfirmationDialogProps,
  "open" | "onOpenChange" | "onConfirm" | "pending"
>;

/**
 * Shared confirmation surface for destructive or irreversible actions.
 *
 * Radix AlertDialog provides the destructive-action keyboard contract,
 * cancel-first focus, outside-interaction guard, and focus return. Product
 * routes keep a promise adapter without falling back to blocking browser UI.
 */
function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  pending = false,
  onConfirm,
}: ConfirmationDialogProps): React.ReactElement {
  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !pending && onOpenChange(nextOpen)}>
      <AlertDialogContent
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button type="button" variant="ghost" disabled={pending}>
              {cancelLabel}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              type="button"
              variant={destructive ? "destructive" : "default"}
              loading={pending}
              loadingLabel={confirmLabel}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Imperative adapter for legacy command handlers that previously used
 * `window.confirm`. The visual/focus contract remains the shared dialog; the
 * caller receives the same boolean decision and can keep its mutation flow
 * surgical while the route is migrated.
 */
function useConfirmationDialog(): {
  confirm: (request: ConfirmationRequest) => Promise<boolean>;
  confirmationDialog: React.ReactElement;
} {
  const [request, setRequest] = React.useState<ConfirmationRequest | null>(null);
  const resolverRef = React.useRef<((confirmed: boolean) => void) | null>(null);

  const finish = React.useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setRequest(null);
  }, []);

  React.useEffect(
    () => () => {
      resolverRef.current?.(false);
      resolverRef.current = null;
    },
    [],
  );

  const confirm = React.useCallback(
    (nextRequest: ConfirmationRequest) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current?.(false);
        resolverRef.current = resolve;
        setRequest(nextRequest);
      }),
    [],
  );

  return {
    confirm,
    confirmationDialog: (
      <ConfirmationDialog
        open={request !== null}
        onOpenChange={(open) => !open && finish(false)}
        title={request?.title ?? "Confirm this action?"}
        description={request?.description ?? "This action requires confirmation."}
        confirmLabel={request?.confirmLabel}
        cancelLabel={request?.cancelLabel}
        destructive={request?.destructive}
        onConfirm={() => finish(true)}
      />
    ),
  };
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  ConfirmationDialog,
  useConfirmationDialog,
};
